import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject, signal, computed } from '@angular/core'

import {
  Enums,
  GeneratedContent,
  PivotRecord,
} from '@cyborg-monorepo/hunter/common'
import { SupportedTools } from '@cyborg-monorepo/hunter/matchboi'

import { VerityEnvironmentService } from '../../../../../environments/verity-environment.service'

@Injectable({ providedIn: 'root' })
export class PivotAPIService {
  private readonly http = inject(HttpClient)
  private readonly verityEnvironmentService = inject(VerityEnvironmentService)

  // ────────────────
  // In‐memory cache of pivots
  // ────────────────
  private readonly _allPivots = signal<PivotRecord[]>([])
  /** Cached list of all known pivots */
  readonly allPivots = computed(() => this._allPivots())

  // ────────────────
  // API Methods
  // ────────────────

  /** Load and cache the full list of pivots */
  loadAllPivots(): void {
    this.http
      .get<
        PivotRecord[]
      >(`${this.verityEnvironmentService.apiUrl}/cyborg-pivots/pivots`)
      .subscribe({
        next: (data) => this._allPivots.set(data),
        error: (err) => console.error('PivotAPI.loadAllPivots', err),
      })
  }

  /** Fetch primary & secondary pivot fields for a hunt category */
  getFieldsForCategory(category: Enums.HuntCategoryEnum) {
    const params = new HttpParams().set('category', category)
    return this.http.get<{
      primary_fields: string[]
      secondary_fields: string[]
    }>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-pivot/hunt-category-fields`,
      { params },
    )
  }

  /** Get pivot‐match suggestions for a given tool and set of fields */
  getPivotMatches(tool: SupportedTools, fields: string[]) {
    let params = new HttpParams().set('tool', tool)
    for (const f of fields) {
      params = params.append('fields', f)
    }
    return this.http.get<PivotRecord[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-pivot/pivot-matches`,
      { params },
    )
  }

  /** Get associated required and optional pivot fields for one or more pivot ids */
  getPivotFields(ids: string[]) {
    let params = new HttpParams()
    for (const id of ids) {
      params = params.append('ids', id)
    }
    return this.http.get<{
      required: string[]
      optional: string[]
    }>(`${this.verityEnvironmentService.apiUrl}/es/cyborg-pivot/pivot-fields`, {
      params,
    })
  }

  /** Get all pivots for a given tool */
  getAllPivotsForTool(tool: SupportedTools) {
    const params = new HttpParams().set('tool', tool)

    return this.http.get<PivotRecord[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-pivot/pivots-for-tool`,
      { params },
    )
  }

  /** Generate new pivot content from the server */
  getContentForPivot(
    pivotUUID: string,
    toolId: string,
    pivotFields: Record<string, string>,
  ) {
    const params = new HttpParams().set('toolId', toolId)
    return this.http.post<GeneratedContent[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-pivot/generate-pivot-content/${pivotUUID}`,
      pivotFields,
      { params },
    )
  }
}
